import { EventBus } from "@saas/components/src/utils/event-bus";
import { uploadLiveLog } from "@saas/components/src/utils/log";
import {
  isWhiteboardAndRecord,
  isAssistant,
  isTeacher,
  isStudent,
  isObjectIncluded,
} from "../utils";
import { getRoomInfo, getUserStatus } from "@saas/service-api/src/live-sdk";
import { debounce } from "@saas/components/src/utils/debounce-async";

import { getRoster } from "@saas/service-api/src/live-sdk";
/**
 * 成员状态管理服务类
 * 负责统一管理 allMemberStatusMap 的更新
 * 处理三个来源的更新：
 * 1. 自定义消息（imService）
 * 2. trtc 回调（liveService）
 * 3. eventbus
 *
 * 使用方式：
 * const memberStatusService = new MemberStatusService(store)
 * memberStatusService.init(imService, liveService, userStatus)
 * memberStatusService.destroy()
 */
export class MemberStatusService {
  constructor(store) {
    this.store = store;
    this.imService = null;
    this.liveService = null;
    this.userStatus = null;

    // 事件处理器
    this._imServiceHandlers = null;
    this._liveServiceHandlers = null;
    this._eventBusHandlers = null;

    // 连接状态标记
    this._isConnectionLost = false;

    // 同步状态请求期间的权限变更记录
    // 用于处理接口请求期间权限被修改的竞态条件
    this._pendingPermissionChanges = null;
    this._syncSelfStatusRequestTime = null;

    // 绑定方法，确保 this 指向正确
    this.updateMemberStatusMap = this.updateMemberStatusMap.bind(this);
    this.removeMemberStatus = this.removeMemberStatus.bind(this);

    uploadLiveLog({
      message: "MemberStatusService constructor",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        component: "MemberStatusService",
        action: "constructor",
      },
    });
  }

  /**
   * 初始化服务
   * @param {Object} imService - IM 服务实例
   * @param {Object} liveService - TRTC 服务实例
   * @param {Object} userStatus - 当前用户状态
   */
  init(imService, liveService, userStatus) {
    uploadLiveLog({
      message: "MemberStatusService init start",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        component: "MemberStatusService",
        action: "init",
        hasImService: !!imService,
        hasLiveService: !!liveService,
        userId: userStatus?.user_id,
      },
    });

    this.imService = imService;
    this.liveService = liveService;
    this.userStatus = userStatus;

    // 初始化事件监听器
    if (this.imService) {
      this.initImServiceListeners();
    }
    if (this.liveService) {
      this.initLiveServiceListeners();
    }
    this.initEventBusListeners();

    this.initRaiseHandList();
    uploadLiveLog({
      message: "MemberStatusService init completed",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        component: "MemberStatusService",
        action: "init",
        hasImService: !!this.imService,
        hasLiveService: !!this.liveService,
      },
    });

    // // 如果已经进入房间，创建台上人员列表
    // if (this.liveService?.isEnterRoomSuccess) {
    //   this.createOnStageList();
    // }
  }

  /**
   * 更新用户状态（用于 watch userStatus）
   * @param {Object} newUserStatus - 新的用户状态
   */
  updateUserStatus(newUserStatus = {}) {
    uploadLiveLog({
      message: "updateUserStatus",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "updateUserStatus",
        component: "MemberStatusService",
        userId: newUserStatus?.user_id,
        hasImService: !!this.imService,
        isEnterRoomSuccess: this.liveService?.isEnterRoomSuccess,
        newUserStatus: { ...newUserStatus },
      },
    });

    this.userStatus = newUserStatus;
    if (
      newUserStatus &&
      this.imService &&
      this.liveService?.isEnterRoomSuccess
    ) {
      const statusData = {
        ...newUserStatus,
        audio_state: newUserStatus.audio_state === 1 ? 1 : 2,
        video_state: newUserStatus.video_state === 1 ? 1 : 2,
      };
      this.updateMemberStatusMap(statusData, "userStatus");
    } else {
      uploadLiveLog({
        message: "updateUserStatus skipped",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "updateUserStatus",
          component: "MemberStatusService",
          reason: !newUserStatus
            ? "no newUserStatus"
            : !this.imService
              ? "no imService"
              : !this.liveService?.isEnterRoomSuccess
                ? "not enter room"
                : "unknown",
        },
      });
    }
  }

  /**
   * 处理进入房间成功
   */
  onEnterRoomSuccess() {
    uploadLiveLog({
      message: "onEnterRoomSuccess",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onEnterRoomSuccess",
        component: "MemberStatusService",
        userId: this.userStatus?.user_id,
      },
    });

    this.updateMemberStatusMap(this.userStatus, "userStatus");
    this.createOnStageList();
  }

  /**
   * 统一更新 allMemberStatusMap 的方法
   * @param {Object|Array} data - 要更新的数据
   *   - 单个对象：{ user_id, ...其他字段 }
   *   - 数组：[{ user_id, ... }, ...]
   * @param {string} source - 更新来源，用于日志记录
   */
  updateMemberStatusMap(data, source = "unknown") {
    if (!data) {
      return;
    }
    // 判断数据是否是数组
    const isArrayData = Array.isArray(data);
    // 判断数据是否是对象
    const isObjectData =
      Object.prototype.toString.call(data) === "[object Object]" &&
      !isArrayData;

    if (!isArrayData && !isObjectData) {
      uploadLiveLog({
        message: "updateMemberStatusMap data is not object or array",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "updateMemberStatusMap",
          component: "MemberStatusService",
          source: source,
          data: data,
        },
      });
      return;
    }
    if (isObjectData && data.user_id && isWhiteboardAndRecord(data.user_id)) {
      uploadLiveLog({
        message: "updateMemberStatusMap data is whiteboard and record",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "updateMemberStatusMap",
          component: "MemberStatusService",
          source: source,
          data: data,
        },
      });
      return;
    }
    if (isObjectData && !data.user_id) {
      uploadLiveLog({
        message: "updateMemberStatusMap data not have user_id",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "updateMemberStatusMap",
          component: "MemberStatusService",
          source: source,
          data: data,
        },
      });
      return;
    }
    // 如果更新的数据中包含自己，需要更新自己的状态，并且判断值和当前的userstatus是否相同，如果相同，则不更新
    const userStatus = this.store.state.live.userStatus;
    const isUpdateSelf = isArrayData
      ? data.some((item) => item.user_id === userStatus?.user_id)
      : data.user_id === userStatus?.user_id;
    if (isUpdateSelf && source !== "userStatus") {
      const statusData = isArrayData
        ? data.find((item) => item.user_id === userStatus.user_id)
        : data;
      const isSame = isObjectIncluded(statusData, userStatus);
      if (!isSame) {
        uploadLiveLog({
          message: "updateMemberStatusMap isUpdateSelf",
          level: "info",
          tag: "allMemberStatusMap",
          extra: {
            action: "updateMemberStatusMap",
            component: "MemberStatusService",
            source: source,
            data: data,
          },
        });
        this.store.commit("live/setUserStatus", {
          ...statusData,
        });
      }
    }

    // 判断更新数据和当前的allMemberStatusMap是否相同，如果相同，则不更新，data有可能是数组，也有可能是对象
    const allMemberStatusMap = this.store.state.live.allMemberStatusMap || {};

    let isChange = false;
    if (isArrayData) {
      isChange = data.some((item) => {
        if (!item.user_id) {
          return false;
        }
        return !isObjectIncluded(item, allMemberStatusMap[item.user_id]);
      });
    } else {
      isChange = !isObjectIncluded(data, allMemberStatusMap[data.user_id]);
    }
    if (isChange) {
      const changedItems = (isArrayData ? data : [data]).filter(
        (item) =>
          item.user_id &&
          !isObjectIncluded(item, allMemberStatusMap[item.user_id])
      )
      const changedFields = Array.from(
        new Set(
          changedItems.flatMap((item) => {
            const beforeStatus = allMemberStatusMap[item.user_id] || {}
            return Object.keys(item).filter(
              (key) => item[key] !== beforeStatus[key]
            )
          })
        )
      ).filter((key) => key !== 'user_id')

      uploadLiveLog({
        message: 'memberStatusChanged',
        level: 'info',
        tag: 'allMemberStatusMap',
        extra: {
          action: 'updateMemberStatusMap',
          component: 'MemberStatusService',
          source,
          userIds: changedItems.map((item) => item.user_id),
          changedFields
        }
      })

      this.store.commit("live/setAllMemberStatusMap", data);

      // 主动通知数据更新
      // 如果是通知当前页面，通过EventBus通知
      // 如果是通知聊天窗口，通过ipcRenderer通知
      const updatedMap = this.store.state.live.allMemberStatusMap || {};
      EventBus.$emit("allMemberStatusMapUpdate", updatedMap);

      if (window.ipcRenderer) {
        window.ipcRenderer.send("chat-state-update", {
          allMemberStatusMap: updatedMap,
          type: "all-member-status",
        });
      }
    }
  }

  /**
   * 删除成员状态
   * @param {string} user_id - 用户ID
   * @param {string} source - 删除来源，用于日志记录
   */
  removeMemberStatus(user_id, source = "unknown") {
    if (!user_id) {
      uploadLiveLog({
        message: "removeMemberStatus skipped - no user_id",
        level: "warn",
        tag: "allMemberStatusMap",
        extra: {
          action: "removeMemberStatus",
          component: "MemberStatusService",
          source: source,
        },
      });
      return;
    }

    uploadLiveLog({
      message: "removeMemberStatus",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "removeMemberStatus",
        component: "MemberStatusService",
        source: source,
        user_id: user_id,
      },
    });

    this.store.commit("live/removeMemberStatusMap", user_id);

    uploadLiveLog({
      message: "removeMemberStatus completed",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "removeMemberStatus",
        component: "MemberStatusService",
        source: source,
        user_id: user_id,
      },
    });
  }

  /**
   * 获取台上人员需要更新的用户ID列表
   * @param {string} fromUserId - 操作人ID
   * @returns {Array} 需要更新的用户ID列表
   */
  getUpdateIds(fromUserId) {
    const onStageMemberIds = this.store.state.live.onStageMemberIds || [];
    if (isTeacher(fromUserId)) {
      return onStageMemberIds.filter((id) => id !== fromUserId);
    } else if (isAssistant(fromUserId)) {
      return onStageMemberIds.filter(
        (id) => !isTeacher(id) && !isAssistant(id)
      );
    }
    return [];
  }

  /**
   * 初始化自定义消息监听器
   */
  initImServiceListeners() {
    if (!this.imService) {
      uploadLiveLog({
        message: "initImServiceListeners skipped - no imService",
        level: "warn",
        tag: "allMemberStatusMap",
        extra: {
          action: "initImServiceListeners",
          component: "MemberStatusService",
        },
      });
      return;
    }

    uploadLiveLog({
      message: "initImServiceListeners start",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "initImServiceListeners",
        component: "MemberStatusService",
      },
    });

    this.removeImServiceListeners();

    this._imServiceHandlers = {
      recvMGLiveRoomCustomMsg: (res) => {
        const { data, userId } = res;
        const fromUserId = userId;
        const msgData = data.data;
        console.log("MemberStatusService recvMGLiveRoomCustomMsg", res);
        if (data.type === "videoMute") {
          this.onReciveVideoMute(msgData, fromUserId, "imService:videoMute");
        } else if (data.type === "audioMute") {
          this.onReciveAudioMute(msgData, fromUserId, "imService:audioMute");
        } else if (data.type === "downstage") {
          this.onDownStage(msgData, fromUserId, "imService:downstage");
        } else if (data.type === "removeStudent") {
          this.onReciveRemoveStudent(
            msgData,
            fromUserId,
            "imService:removeStudent"
          );
        } else if (data.type === "member_hand_up") {
          this.onReciveUserHandUp(
            msgData,
            fromUserId,
            "imService:member_hand_up"
          );
        } else if (data.type === "userStatus") {
          this.onReciveUserStatusChange(
            msgData,
            fromUserId,
            "imService:userStatus"
          );
        } else if (data.type === "updateNickname") {
          this.onReciveUpdateNickname(
            msgData,
            fromUserId,
            "imService:updateNickname"
          );
        } else if (data.type === "whiteboard") {
          this.onReciveWhiteboard(msgData, fromUserId, "imService:whiteboard");
        } else if (data.type === "messageMute") {
          this.onReciveMessageMute(
            msgData,
            fromUserId,
            "imService:messageMute"
          );
        } else if (data.type === "initUserStatus") {
          this.onReciveInitUserStatus(
            msgData,
            fromUserId,
            "imService:initUserStatus"
          );
        } else if (data.type === "xhRemoteEnterRoom") {
          this.onReciveXhRemoteEnterRoom(
            msgData,
            fromUserId,
            "imService:xhRemoteEnterRoom"
          );
        } else if (data.type === "trophy") {
          this.onReciveTrophy(msgData, fromUserId, "imService:trophy");
        }
      },
    };

    this.imService.on(
      "recvMGLiveRoomCustomMsg",
      this._imServiceHandlers.recvMGLiveRoomCustomMsg
    );

    uploadLiveLog({
      message: "initImServiceListeners completed",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "initImServiceListeners",
        component: "MemberStatusService",
      },
    });
  }

  /**
   * 移除自定义消息监听器
   */
  removeImServiceListeners() {
    if (this.imService && this._imServiceHandlers) {
      const eventNames = Object.keys(this._imServiceHandlers);
      eventNames.forEach((eventName) => {
        this.imService.off(eventName, this._imServiceHandlers[eventName]);
      });
      uploadLiveLog({
        message: "removeImServiceListeners",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "removeImServiceListeners",
          component: "MemberStatusService",
          eventCount: eventNames.length,
          eventNames: eventNames,
        },
      });
    }
    this._imServiceHandlers = null;
  }

  /**
   * 初始化 trtc 回调监听器
   */
  initLiveServiceListeners() {
    if (!this.liveService) {
      uploadLiveLog({
        message: "initLiveServiceListeners skipped - no liveService",
        level: "warn",
        tag: "allMemberStatusMap",
        extra: {
          action: "initLiveServiceListeners",
          component: "MemberStatusService",
        },
      });
      return;
    }

    uploadLiveLog({
      message: "initLiveServiceListeners start",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "initLiveServiceListeners",
        component: "MemberStatusService",
      },
    });

    this.removeLiveServiceListeners();

    this._liveServiceHandlers = {
      remoteUserEnterRoom: (res) => {
        this.onReciveRemoteUserEnterRoom(res);
      },
      remoteUserLeaveRoom: (res) => {
        this.onReciveRemoteUserLeaveRoom(res);
      },
      userVideoAvailable: (res) => {
        this.onReciveUserVideoAvailable(res);
      },
      userAudioAvailable: (res) => {
        this.onReciveUserAudioAvailable(res);
      },
      connectionLost: () => {
        this.onReciveConnectionLost();
      },
      connectionRecovery: () => {
        this.onReciveConnectionRecovery();
      },
    };

    const eventNames = Object.keys(this._liveServiceHandlers);
    eventNames.forEach((eventName) => {
      this.liveService.on(eventName, this._liveServiceHandlers[eventName]);
    });

    uploadLiveLog({
      message: "initLiveServiceListeners completed",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "initLiveServiceListeners",
        component: "MemberStatusService",
        eventCount: eventNames.length,
        eventNames: eventNames,
      },
    });
  }

  /**
   * 移除 trtc 回调监听器
   */
  removeLiveServiceListeners() {
    if (this.liveService && this._liveServiceHandlers) {
      const eventNames = Object.keys(this._liveServiceHandlers);
      eventNames.forEach((eventName) => {
        this.liveService.off(eventName, this._liveServiceHandlers[eventName]);
      });
      uploadLiveLog({
        message: "removeLiveServiceListeners",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "removeLiveServiceListeners",
          component: "MemberStatusService",
          eventCount: eventNames.length,
          eventNames: eventNames,
        },
      });
    }
    this._liveServiceHandlers = null;
  }

  /**
   * 初始化 eventbus 监听器
   */
  initEventBusListeners() {
    uploadLiveLog({
      message: "initEventBusListeners start",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "initEventBusListeners",
        component: "MemberStatusService",
      },
    });

    this.removeEventBusListeners();
    this._eventBusHandlers = {
      downstage: (res) => {
        this.onDownStage(res, this.userStatus?.user_id, "eventbus:downstage");
      },
      removeStudent: (res) => {
        this.onReciveRemoveStudent(
          res,
          this.userStatus?.user_id,
          "eventbus:removeStudent"
        );
      },
      member_hand_up: (res) => {
        this.onReciveUserHandUp(
          res,
          this.userStatus?.user_id,
          "eventbus:member_hand_up"
        );
      },
      videoMute: (res) => {
        this.onReciveVideoMute(
          res,
          this.userStatus?.user_id,
          "eventbus:videoMute"
        );
      },
      audioMute: (res) => {
        this.onReciveAudioMute(
          res,
          this.userStatus?.user_id,
          "eventbus:audioMute"
        );
      },
      whiteboard: (res) => {
        this.onReciveWhiteboard(
          res,
          this.userStatus?.user_id,
          "eventbus:whiteboard"
        );
      },
      updateNickname: (res) => {
        this.onReciveUpdateNickname(
          res,
          this.userStatus?.user_id,
          "eventbus:updateNickname"
        );
      },
      messageMute: (res) => {
        this.onReciveMessageMute(
          res,
          this.userStatus?.user_id,
          "eventbus:messageMute"
        );
      },
      trophy: (res) => {
        this.onReciveTrophy(res, this.userStatus?.user_id, "eventbus:trophy");
      },
    };

    const eventNames = Object.keys(this._eventBusHandlers);
    eventNames.forEach((eventName) => {
      EventBus.$on(eventName, this._eventBusHandlers[eventName]);
    });

    uploadLiveLog({
      message: "initEventBusListeners completed",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "initEventBusListeners",
        component: "MemberStatusService",
        eventCount: eventNames.length,
        eventNames: eventNames,
      },
    });
  }

  /**
   * 移除 eventbus 监听器
   */
  removeEventBusListeners() {
    if (this._eventBusHandlers) {
      const eventNames = Object.keys(this._eventBusHandlers);
      eventNames.forEach((eventName) => {
        EventBus.$off(eventName, this._eventBusHandlers[eventName]);
      });
      uploadLiveLog({
        message: "removeEventBusListeners",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "removeEventBusListeners",
          component: "MemberStatusService",
          eventCount: eventNames.length,
          eventNames: eventNames,
        },
      });
    }
    this._eventBusHandlers = null;
  }

  // ========== 事件处理方法 ==========

  /**
   * 初始化用户状态事件
   */
  onReciveInitUserStatus(res, fromUserId, fromSource) {
    this.updateMemberStatusMap(
      {
        ...res,
        user_id: res.user_id,
        is_up_stage: isTeacher(res.user_id) ? 1 : res.is_up_stage,
        audio_state: res.audio_state === 1 ? 1 : 2,
        video_state: res.video_state === 1 ? 1 : 2,
        status: 1,
      },
      fromSource
    );
  }

  /**
   * 用户状态变化事件 只用非权限字段，
   * 为了兼容老版本，需要在这同步一下上台权限字段
   */
  onReciveUserStatusChange(res, fromUserId, fromSource) {
    const {
      user_id,
      audio_state,
      video_state,
      nickname,
      avatar,
      trophy_count,
      is_up_stage,
    } = res;
    const allMemberStatusMap = this.store.state.live.allMemberStatusMap || {};
    const memberStatus = allMemberStatusMap[user_id] || {};
    const status = {
      user_id: user_id,
      audio_state: audio_state === 1 ? 1 : 2,
      video_state: video_state === 1 ? 1 : 2,
      nickname: nickname,
      avatar: avatar,
      trophy_count: trophy_count,
      status: 1,
    };
    if (
      !Object.prototype.hasOwnProperty.call(memberStatus, "is_up_stage") &&
      is_up_stage === 1
    ) {
      // 兼容老版本 如果没有这个字段，并且上台权限为1，则同步上台权限字段
      status.is_up_stage = 1;
    }
    this.updateMemberStatusMap(status, fromSource);
  }

  /**
   * 上下台事件
   */
  onDownStage(res, fromUserId, fromSource) {
    const { userId, isDown, isWhiteboard, isSendAudio, isSendVideo } = res;
    const userStatus = this.store.state.live.userStatus;
    uploadLiveLog({
      message: "onDownStage",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onDownStage",
        component: "MemberStatusService",
        fromUserId: fromUserId,
        fromSource: fromSource,
        userId: userId,
        isDown: isDown,
        isAllUsers: userId === "*",
        data: res,
      },
    });

    // 如果是在同步自己状态期间，记录权限变更
    const permissionChanges = {
      is_up_stage: isDown ? 2 : 1,
    };
    if (isDown) {
      permissionChanges.is_send_audio = 2;
      permissionChanges.is_send_video = 2;
    } else {
      if (isSendAudio !== undefined) {
        permissionChanges.is_send_audio = isSendAudio === 1 ? 1 : 2;
      }
      if (isSendVideo !== undefined) {
        permissionChanges.is_send_video = isSendVideo === 1 ? 1 : 2;
      }
      if (isWhiteboard !== undefined) {
        permissionChanges.is_whiteboard = isWhiteboard === 1 ? 1 : 2;
      }
    }
    this.recordPermissionChangeIfNeeded(
      userId,
      permissionChanges,
      "onDownStage"
    );

    const allMemberStatusMap = this.store.state.live.allMemberStatusMap || {};

    if (userId === "*") {
      // 全员下台
      const ids = this.getUpdateIds(fromUserId);
      const memberStatusList = ids.map((id) => {
        return {
          user_id: id,
          is_up_stage: 2,
          is_send_audio: 2,
          is_send_video: 2,
          audio_state: 2,
          video_state: 2,
        };
      });
      uploadLiveLog({
        message: "onDownStage all users",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "onDownStage",
          component: "MemberStatusService",
          ids: ids,
          memberStatusList: memberStatusList,
        },
      });
      this.updateMemberStatusMap(memberStatusList, fromSource);
    } else {
      // 单个用户上下台
      const memberStatus = allMemberStatusMap[userId];
      if (isDown) {
        uploadLiveLog({
          message: "onDownStage user down",
          level: "info",
          tag: "allMemberStatusMap",
          extra: {
            action: "onDownStage",
            component: "MemberStatusService",
          },
        });
        // 下台
        this.updateMemberStatusMap(
          {
            user_id: userId,
            is_up_stage: 2,
            is_send_audio: 2,
            is_send_video: 2,
            audio_state: 2,
            video_state: 2,
          },
          fromSource
        );
        this.store.commit("live/setShowOpenAudioDialog", false);
        this.store.commit("live/setShowOpenVideoDialog", false);
        uploadLiveLog({
          message:
            "onDownStage user down setShowOpenAudioDialog and setShowOpenVideoDialog false",
          level: "info",
          tag: "allMemberStatusMap",
          extra: {
            action: "onDownStage",
            component: "MemberStatusService",
          },
        });
      } else {
        // 上台
        uploadLiveLog({
          message: "onDownStage user up",
          level: "info",
          tag: "allMemberStatusMap",
          extra: {
            action: "onDownStage",
            component: "MemberStatusService",
            memberStatus: memberStatus,
            userId: userId,
            isWhiteboard: isWhiteboard,
            isSendAudio: isSendAudio,
            isSendVideo: isSendVideo,
            isSelf: userId === userStatus?.user_id,
            isTeacher: isTeacher(userStatus.user_id),
          },
        });
        this.updateMemberStatusMap(
          {
            user_id: userId,
            is_up_stage: 1,
            is_whiteboard:
              isWhiteboard === 1 ? 1 : memberStatus?.is_whiteboard || 2,
            is_send_audio: isSendAudio === 1 ? 1 : 2,
            is_send_video: isSendVideo === 1 ? 1 : 2,
          },
          fromSource
        );

        if (userId === userStatus?.user_id) {
          // 自己上台
          if (isSendAudio === 1 && !isTeacher(userStatus.user_id)) {
            this.store.commit("live/setShowOpenAudioDialog", true);
            uploadLiveLog({
              message: "onDownStage user up setShowOpenAudioDialog true",
              level: "info",
              tag: "allMemberStatusMap",
              extra: {
                action: "onDownStage",
                component: "MemberStatusService",
              },
            });
          } else {
            this.store.commit("live/setShowOpenAudioDialog", false);
            uploadLiveLog({
              message: "onDownStage user up setShowOpenAudioDialog false",
              level: "info",
              tag: "allMemberStatusMap",
              extra: {
                action: "onDownStage",
                component: "MemberStatusService",
              },
            });
          }
          if (isSendVideo === 1 && !isTeacher(userStatus.user_id)) {
            this.store.commit("live/setShowOpenVideoDialog", true);
            uploadLiveLog({
              message: "onDownStage user up setShowOpenVideoDialog true",
              level: "info",
              tag: "allMemberStatusMap",
              extra: {
                action: "onDownStage",
                component: "MemberStatusService",
              },
            });
          } else {
            this.store.commit("live/setShowOpenVideoDialog", false);
            uploadLiveLog({
              message: "onDownStage user up setShowOpenVideoDialog false",
              level: "info",
              tag: "allMemberStatusMap",
              extra: {
                action: "onDownStage",
                component: "MemberStatusService",
              },
            });
          }
        }
      }
    }
  }

  /**
   * 摄像头权限开/关事件
   */
  onReciveVideoMute(res, fromUserId, fromSource) {
    const { userId, isMute } = res;
    const userStatus = this.store.state.live.userStatus;
    uploadLiveLog({
      message: "onReciveVideoMute",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveVideoMute",
        component: "MemberStatusService",
        fromUserId: fromUserId,
        fromSource: fromSource,
        userId: userId,
        isMute: isMute,
        isAllUsers: userId === "*",
      },
    });

    // 如果是在同步自己状态期间，记录权限变更
    this.recordPermissionChangeIfNeeded(
      userId,
      { is_send_video: isMute ? 2 : 1 },
      "onReciveVideoMute"
    );

    const allMemberStatusMap = this.store.state.live.allMemberStatusMap || {};

    if (userId === "*") {
      // 全员开/关摄像头权限
      const ids = this.getUpdateIds(fromUserId);

      const memberStatusList = ids.map((id) => {
        const memberStatus = allMemberStatusMap[id];
        return {
          user_id: id,
          is_send_video: isMute ? 2 : 1,
          video_state: isMute ? 2 : memberStatus?.video_state || 2,
        };
      });
      uploadLiveLog({
        message: "onReciveVideoMute all users",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "onReciveVideoMute",
          component: "MemberStatusService",
          ids: ids,
          isMute: isMute,
          memberStatusList: memberStatusList,
        },
      });
      this.updateMemberStatusMap(memberStatusList, fromSource);

      if (!isMute) {
        if (
          isTeacher(fromUserId) &&
          !isTeacher(userStatus?.user_id) &&
          userStatus?.is_up_stage === 1 &&
          userStatus?.video_state !== 1
        ) {
          // 老师操作全员打开摄像头，自己不是老师，并且自己在台上，需要打开摄像头弹窗
          this.store.commit("live/setShowOpenVideoDialog", true);
          uploadLiveLog({
            message: "onReciveVideoMute all users setShowOpenVideoDialog true",
            level: "info",
            tag: "allMemberStatusMap",
            extra: {
              action: "onReciveVideoMute",
              component: "MemberStatusService",
            },
          });
        } else if (
          isAssistant(fromUserId) &&
          isStudent(userStatus?.user_id) &&
          userStatus?.is_up_stage === 1 &&
          userStatus?.video_state !== 1
        ) {
          // 助教操作全员打开摄像头，自己是学生，并且在台上，需要打开摄像头弹窗
          this.store.commit("live/setShowOpenVideoDialog", true);
          uploadLiveLog({
            message: "onReciveVideoMute all users setShowOpenVideoDialog true",
            level: "info",
            tag: "allMemberStatusMap",
            extra: {
              action: "onReciveVideoMute",
              component: "MemberStatusService",
            },
          });
        } else {
          this.store.commit("live/setShowOpenVideoDialog", false);
          uploadLiveLog({
            message: "onReciveVideoMute all users setShowOpenVideoDialog false",
            level: "info",
            tag: "allMemberStatusMap",
            extra: {
              action: "onReciveVideoMute",
              component: "MemberStatusService",
            },
          });
        }
      } else {
        this.store.commit("live/setShowOpenVideoDialog", false);
        uploadLiveLog({
          message: "onReciveVideoMute all users setShowOpenVideoDialog false",
          level: "info",
          tag: "allMemberStatusMap",
          extra: {
            action: "onReciveVideoMute",
            component: "MemberStatusService",
          },
        });
      }
    } else {
      // 单个用户开/关摄像头权限
      const memberStatus = allMemberStatusMap[userId];
      uploadLiveLog({
        message: "onReciveVideoMute user",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "onReciveVideoMute",
          component: "MemberStatusService",
          userId: userId,
          isMute: isMute,
          memberStatus: memberStatus,
        },
      });
      this.updateMemberStatusMap(
        {
          user_id: userId,
          is_send_video: isMute ? 2 : 1,
          video_state: isMute ? 2 : memberStatus?.video_state || 2,
        },
        fromSource
      );

      if (!isMute && userStatus?.user_id === userId) {
        this.store.commit("live/setShowOpenVideoDialog", true);
        uploadLiveLog({
          message: "onReciveVideoMute user setShowOpenVideoDialog true",
          level: "info",
          tag: "allMemberStatusMap",
          extra: {
            action: "onReciveVideoMute",
            component: "MemberStatusService",
          },
        });
      } else {
        this.store.commit("live/setShowOpenVideoDialog", false);
        uploadLiveLog({
          message: "onReciveVideoMute user setShowOpenVideoDialog false",
          level: "info",
          tag: "allMemberStatusMap",
          extra: {
            action: "onReciveVideoMute",
            component: "MemberStatusService",
          },
        });
      }
    }
  }

  /**
   * 麦克风开关事件
   */
  onReciveAudioMute(res, fromUserId, fromSource) {
    const { userId, isMute } = res;
    const userStatus = this.store.state.live.userStatus;
    uploadLiveLog({
      message: "onReciveAudioMute",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveAudioMute",
        component: "MemberStatusService",
        fromUserId: fromUserId,
        fromSource: fromSource,
        userId: userId,
        isMute: isMute,
        isAllUsers: userId === "*",
      },
    });

    // 如果是在同步自己状态期间，记录权限变更
    this.recordPermissionChangeIfNeeded(
      userId,
      { is_send_audio: isMute ? 2 : 1 },
      "onReciveAudioMute"
    );

    const allMemberStatusMap = this.store.state.live.allMemberStatusMap || {};

    if (userId === "*") {
      // 全员开/关麦克风权限
      const ids = this.getUpdateIds(fromUserId);
      const memberStatusList = ids.map((id) => {
        const memberStatus = allMemberStatusMap[id];
        return {
          user_id: id,
          is_send_audio: isMute ? 2 : 1,
          audio_state: isMute ? 2 : memberStatus?.audio_state || 2,
        };
      });
      uploadLiveLog({
        message: "onReciveAudioMute all users",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "onReciveAudioMute",
          component: "MemberStatusService",
          ids: ids,
          isMute: isMute,
          memberStatusList: memberStatusList,
        },
      });
      this.updateMemberStatusMap(memberStatusList, fromSource);

      if (!isMute) {
        if (
          isTeacher(fromUserId) &&
          !isTeacher(userStatus?.user_id) &&
          userStatus?.is_up_stage === 1 &&
          userStatus?.audio_state !== 1
        ) {
          // 老师操作全员打开麦克风，自己不是老师，需要打开麦克风弹窗
          this.store.commit("live/setShowOpenAudioDialog", true);
          uploadLiveLog({
            message: "onReciveAudioMute all users setShowOpenAudioDialog true",
            level: "info",
            tag: "allMemberStatusMap",
            extra: {
              action: "onReciveAudioMute",
              component: "MemberStatusService",
              isTeacher: isTeacher(fromUserId),
              isAssistant: isAssistant(fromUserId),
              isStudent: isStudent(userStatus?.user_id),
            },
          });
        } else if (
          isAssistant(fromUserId) &&
          isStudent(userStatus?.user_id) &&
          userStatus?.is_up_stage === 1 &&
          userStatus?.audio_state !== 1
        ) {
          // 助教操作全员打开麦克风，自己是学生，需要打开麦克风弹窗
          this.store.commit("live/setShowOpenAudioDialog", true);
          uploadLiveLog({
            message: "onReciveAudioMute all users setShowOpenAudioDialog true",
            level: "info",
            tag: "allMemberStatusMap",
            extra: {
              action: "onReciveAudioMute",
              component: "MemberStatusService",
              isTeacher: isTeacher(fromUserId),
              isAssistant: isAssistant(fromUserId),
              isStudent: isStudent(userStatus?.user_id),
            },
          });
        } else {
          this.store.commit("live/setShowOpenAudioDialog", false);
          uploadLiveLog({
            message: "onReciveAudioMute all users setShowOpenAudioDialog false",
            level: "info",
            tag: "allMemberStatusMap",
            extra: {
              action: "onReciveAudioMute",
              component: "MemberStatusService",
              isMute: isMute,
            },
          });
        }
      } else {
        this.store.commit("live/setShowOpenAudioDialog", false);
        uploadLiveLog({
          message: "onReciveAudioMute all users setShowOpenAudioDialog false",
          level: "info",
          tag: "allMemberStatusMap",
          extra: {
            action: "onReciveAudioMute",
            component: "MemberStatusService",
            isMute: isMute,
          },
        });
      }
    } else {
      // 单个用户开/关麦克风权限
      const memberStatus = allMemberStatusMap[userId];
      uploadLiveLog({
        message: "onReciveAudioMute user",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "onReciveAudioMute",
          component: "MemberStatusService",
          userId: userId,
          isMute: isMute,
          memberStatus: memberStatus,
        },
      });
      this.updateMemberStatusMap(
        {
          user_id: userId,
          is_send_audio: isMute ? 2 : 1,
          audio_state: isMute ? 2 : memberStatus?.audio_state || 2,
        },
        fromSource
      );

      if (!isMute && userStatus?.user_id === userId) {
        this.store.commit("live/setShowOpenAudioDialog", true);
        uploadLiveLog({
          message: "onReciveAudioMute user setShowOpenAudioDialog true",
          level: "info",
          tag: "allMemberStatusMap",
          extra: {
            action: "onReciveAudioMute",
            component: "MemberStatusService",
          },
        });
      } else {
        this.store.commit("live/setShowOpenAudioDialog", false);
        uploadLiveLog({
          message: "onReciveAudioMute user setShowOpenAudioDialog false",
          level: "info",
          tag: "allMemberStatusMap",
          extra: {
            action: "onReciveAudioMute",
            component: "MemberStatusService",
          },
        });
      }
    }
  }

  /**
   * 移除学员事件
   */
  onReciveRemoveStudent(res, fromUserId, fromSource) {
    const { userId } = res;

    uploadLiveLog({
      message: "onReciveRemoveStudent",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveRemoveStudent",
        component: "MemberStatusService",
        fromUserId: fromUserId,
        fromSource: fromSource,
        userId: userId,
      },
    });

    // 删除成员状态
    // this.removeMemberStatus(userId);
    this.updateMemberStatusMap({
      user_id: userId,
      is_up_stage: 2,
      is_send_audio: 2,
      is_send_video: 2,
      audio_state: 2,
      video_state: 2,
    });
  }

  /**
   * 举手事件
   */
  onReciveUserHandUp(res, fromUserId, fromSource) {
    const { user_id } = res;

    uploadLiveLog({
      message: "onReciveUserHandUp",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveUserHandUp",
        component: "MemberStatusService",
        fromUserId: fromUserId,
        fromSource: fromSource,
        userId: user_id,
      },
    });

    this.updateMemberStatusMap({
      user_id: user_id,
      is_hand_up: 1,
    });

    setTimeout(() => {
      uploadLiveLog({
        message: "onReciveUserHandUp timeout - reset hand_up",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "onReciveUserHandUp",
          component: "MemberStatusService",
          userId: user_id,
        },
      });

      this.updateMemberStatusMap({
        user_id: user_id,
        is_hand_up: 2,
      });
    }, 10000);
  }

  /**
   * 白板权限开/关事件
   */
  onReciveWhiteboard(res, fromUserId, fromSource) {
    const { userId, isWhiteboard } = res;
    const userStatus = this.store.state.live.userStatus;

    uploadLiveLog({
      message: "onReciveWhiteboard",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveWhiteboard",
        component: "MemberStatusService",
        fromUserId: fromUserId,
        fromSource: fromSource,
        userId: userId,
        isWhiteboard: isWhiteboard,
      },
    });

    // 如果是在同步自己状态期间，记录权限变更
    this.recordPermissionChangeIfNeeded(
      userId,
      { is_whiteboard: isWhiteboard ? 1 : 2 },
      "onReciveWhiteboard"
    );

    this.updateMemberStatusMap(
      {
        user_id: userId,
        is_whiteboard: isWhiteboard ? 1 : 2,
      },
      fromSource
    );
  }

  /**
   * 昵称更新事件
   */
  onReciveUpdateNickname(res, fromUserId, fromSource) {
    const { userId, nickname } = res;

    uploadLiveLog({
      message: "onReciveUpdateNickname",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveUpdateNickname",
        component: "MemberStatusService",
        fromUserId: fromUserId,
        fromSource: fromSource,
        userId: userId,
        nickname: nickname,
      },
    });

    // if (userId === this.userStatus?.user_id) {
    this.updateMemberStatusMap(
      {
        user_id: userId,
        nickname: nickname,
      },
      fromSource
    );
    // }
  }

  /**
   * 消息禁言事件开关
   */
  onReciveMessageMute(res, fromUserId, fromSource) {
    const { userId, isMute } = res;
    const userStatus = this.store.state.live.userStatus;

    uploadLiveLog({
      message: "onReciveMessageMute",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveMessageMute",
        component: "MemberStatusService",
        fromUserId: fromUserId,
        fromSource: fromSource,
        userId: userId,
        isMute: isMute,
        isAllUsers: userId === "*",
      },
    });

    // 如果是在同步自己状态期间，记录权限变更
    this.recordPermissionChangeIfNeeded(
      userId,
      { is_send_msg: isMute ? 2 : 1 },
      "onReciveMessageMute"
    );

    const allMemberStatusMap = this.store.state.live.allMemberStatusMap || {};

    if (userId === "*") {
      // 全员开/关消息权限
      let list = [];
      if (isTeacher(fromUserId)) {
        list = Object.values(allMemberStatusMap).filter(
          (item) => item.user_role !== 1
        );
      } else if (isAssistant(fromUserId)) {
        // 助教操作全员开/关闭消息，学生状态更改
        list = Object.values(allMemberStatusMap).filter(
          (item) => item.user_role === 3
        );
      }
      list.forEach((item) => {
        this.updateMemberStatusMap({
          user_id: item.user_id,
          is_send_msg: isMute ? 2 : 1,
        });
      });
    } else {
      // 单个用户开/关消息权限
      this.updateMemberStatusMap(
        {
          user_id: userId,
          is_send_msg: isMute ? 2 : 1,
        },
        fromSource
      );
    }
  }

  /**
   * 奖杯更新事件
   */
  onReciveTrophy(res, fromUserId, fromSource) {
    const { userId } = res;

    uploadLiveLog({
      message: "onReciveTrophy",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveTrophy",
        component: "MemberStatusService",
        fromUserId: fromUserId,
        fromSource: fromSource,
        userId: userId,
        isAllUsers: userId === "*",
      },
    });

    const allMemberStatusMap = this.store.state.live.allMemberStatusMap || {};
    const userStatus = this.store.state.live.userStatus;
    if (userId === "*") {
      // 全员获得奖杯
      // const memberStatusList = Object.values(allMemberStatusMap).map(
      //   (memberStatus) => {
      //     return {
      //       ...memberStatus,
      //       trophy_count: (memberStatus.trophy_count || 0) + 1,
      //     };
      //   }
      // );

      this.updateMemberStatusMap(
        {
          user_id: userStatus.user_id,
          trophy_count: (userStatus.trophy_count || 0) + 1,
        },
        fromSource
      );
    } else {
      // 单个用户获得奖杯
      const memberStatus = allMemberStatusMap[userId];
      uploadLiveLog({
        message: "onReciveTrophy skipped - memberStatus not found",
        level: "warn",
        tag: "allMemberStatusMap",
        extra: {
          action: "onReciveTrophy",
          component: "MemberStatusService",
          userId: userId,
          memberStatus: memberStatus,
        },
      });
      if (userId === userStatus.user_id) {
        this.updateMemberStatusMap(
          {
            user_id: userId,
            trophy_count: (userStatus.trophy_count || 0) + 1,
          },
          fromSource
        );
      }
      // this.updateMemberStatusMap(
      //   {
      //     user_id: userId,
      //     trophy_count: (memberStatus?.trophy_count || 0) + 1,
      //   },
      //   fromSource
      // );
    }
  }

  /**
   * 初始化的时候把已经在台上的人员创建出来
   */
  createOnStageList() {
    const roomInfo = this.store.state.live.roomInfo;
    if (!roomInfo?.id) {
      uploadLiveLog({
        message: "createOnStageList skipped - no roomInfo.id",
        level: "warn",
        tag: "allMemberStatusMap",
        extra: {
          action: "createOnStageList",
          component: "MemberStatusService",
        },
      });
      return;
    }

    uploadLiveLog({
      message: "createOnStageList start",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "createOnStageList",
        component: "MemberStatusService",
        roomId: roomInfo.id,
      },
    });

    getRoomInfo({
      room_id: roomInfo.id,
    })
      .then((res) => {
        const list = res.data?.user_list || [];
        uploadLiveLog({
          message: "createOnStageList getRoomInfo success",
          level: "info",
          tag: "allMemberStatusMap",
          extra: {
            action: "createOnStageList",
            component: "MemberStatusService",
            roomId: roomInfo.id,
            userCount: list.length,
          },
        });

        const allMemberStatusMap =
          this.store.state.live.allMemberStatusMap || {};
        list.forEach((item) => {
          const statusData = allMemberStatusMap[item.user_id] || {};
          this.updateMemberStatusMap(
            {
              ...item,
              user_id: item.user_id,
              audio_state: item.is_send_audio !== 1 ? 2 : item.audio_state,
              video_state: item.is_send_video !== 1 ? 2 : item.video_state,
              ...statusData,
            },
            "createOnStageList"
          );
        });

        uploadLiveLog({
          message: "createOnStageList completed",
          level: "info",
          tag: "allMemberStatusMap",
          extra: {
            action: "createOnStageList",
            component: "MemberStatusService",
            roomId: roomInfo.id,
            userCount: list.length,
          },
        });
      })
      .catch((error) => {
        uploadLiveLog({
          message: "createOnStageList failed",
          level: "error",
          tag: "allMemberStatusMap",
          extra: {
            action: "createOnStageList",
            component: "MemberStatusService",
            roomId: roomInfo.id,
            error: error.message || String(error),
            stack: error.stack,
          },
        });
        console.error("创建台上人员列表失败", error);
      });
  }

  /**
   * 用户进入房间（IM 消息）
   */
  onReciveXhRemoteEnterRoom(res, fromUserId, fromSource) {
    const { userId } = res;

    uploadLiveLog({
      message: "onReciveXhRemoteEnterRoom",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveXhRemoteEnterRoom",
        component: "MemberStatusService",
        fromUserId: fromUserId,
        fromSource: fromSource,
        userId: userId,
      },
    });

    this.updateMemberStatusMap(
      {
        user_id: userId,
        status: 1,
      },
      fromSource
    );
  }

  /**
   * 用户进入房间（TRTC 回调）
   */
  onReciveRemoteUserEnterRoom(res) {
    uploadLiveLog({
      message: "onReciveRemoteUserEnterRoom",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveRemoteUserEnterRoom",
        component: "MemberStatusService",
        data: res,
      },
    });
    const { userID } = res;
    this.updateMemberStatusMap(
      {
        user_id: userID,
        status: 1,
      },
      "liveService:remoteUserEnterRoom"
    );
  }

  /**
   * 用户离开房间（TRTC 回调）
   */
  onReciveRemoteUserLeaveRoom(res) {
    const { userID } = res;

    uploadLiveLog({
      message: "onReciveRemoteUserLeaveRoom",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveRemoteUserLeaveRoom",
        component: "MemberStatusService",
        userId: userID,
      },
    });

    this.updateMemberStatusMap(
      {
        user_id: userID,
        status: 0,
        is_recive_video_stream: 2,
        is_recive_audio_stream: 2,
      },
      "liveService:remoteUserLeaveRoom"
    );
  }

  /**
   * 用户视频可用/不可用（TRTC 回调）
   */
  onReciveUserVideoAvailable(res) {
    const { userID, available, streamType } = res;

    if (streamType === "sub") {
      uploadLiveLog({
        message: "onReciveUserVideoAvailable skipped - sub stream",
        level: "info",
        tag: "allMemberStatusMap",
        extra: {
          action: "onReciveUserVideoAvailable",
          component: "MemberStatusService",
          userId: userID,
          streamType: streamType,
        },
      });
      return;
    }

    uploadLiveLog({
      message: "onReciveUserVideoAvailable",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveUserVideoAvailable",
        component: "MemberStatusService",
        userId: userID,
        available: available,
        streamType: streamType,
      },
    });

    this.updateMemberStatusMap(
      {
        user_id: userID,
        is_recive_video_stream: available === 1 ? 1 : 2,
      },
      "liveService:userVideoAvailable"
    );
  }

  /**
   * 用户音频可用/不可用（TRTC 回调）
   */
  onReciveUserAudioAvailable(res) {
    const { userID, available } = res;

    uploadLiveLog({
      message: "onReciveUserAudioAvailable",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveUserAudioAvailable",
        component: "MemberStatusService",
        userId: userID,
        available: available,
      },
    });

    this.updateMemberStatusMap(
      {
        user_id: userID,
        is_recive_audio_stream: available === 1 ? 1 : 2,
      },
      "liveService:userAudioAvailable"
    );
  }

  /**
   * 连接断开事件 （TRTC 回调）
   */
  onReciveConnectionLost = debounce(function () {
    console.log("onReciveConnection___Lost");
    uploadLiveLog({
      message: "onReciveConnectionLost",
      level: "warn",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveConnectionLost",
        component: "MemberStatusService",
      },
    });
    // 标记连接断开，用于后续状态同步
    this._isConnectionLost = true;
  }, 1000);

  /**
   * 连接恢复事件（TRTC 回调）
   */
  onReciveConnectionRecovery = debounce(function () {
    console.log("onReciveConnection___Recovery");
    uploadLiveLog({
      message: "onReciveConnectionRecovery",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "onReciveConnectionRecovery",
        component: "MemberStatusService",
      },
    });
    // 连接恢复后，同步状态以规避状态不一致问题
    if (this._isConnectionLost) {
      this.syncRoomStatusAfterReconnect();
      this._isConnectionLost = false;
    }
  }, 1000);

  /**
   * 连接恢复后同步房间状态
   * 解决连接断开期间可能丢失的广播消息导致的状态不一致问题
   * 通过广播消息通知所有人发送自己的最新状态
   */
  syncRoomStatusAfterReconnect() {
    const roomInfo = this.store.state.live.roomInfo;
    if (!roomInfo?.id) {
      uploadLiveLog({
        message: "syncRoomStatusAfterReconnect skipped - no roomInfo.id",
        level: "warn",
        tag: "allMemberStatusMap",
        extra: {
          action: "syncRoomStatusAfterReconnect",
          component: "MemberStatusService",
        },
      });
      return;
    }

    uploadLiveLog({
      message: "syncRoomStatusAfterReconnect start",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "syncRoomStatusAfterReconnect",
        component: "MemberStatusService",
        roomId: roomInfo.id,
      },
    });

    // 1. 从接口获取自己的状态（因为自己发送的广播消息自己收不到）
    this.syncSelfStatus(roomInfo.id);

    // 2. 通过 IM 广播消息，通知其他人发送自己的最新状态
    this.requestOtherUsersStatus();
  }

  /**
   * 记录请求期间的权限变更（用于处理竞态条件）
   * @param {string} userId - 被修改权限的用户ID
   * @param {Object} permissionChanges - 权限变更对象，如 { is_send_audio: 1, is_send_video: 2 }
   * @param {string} action - 操作名称，用于日志
   */
  recordPermissionChangeIfNeeded(userId, permissionChanges, action) {
    // 如果不在同步状态期间，直接返回
    if (!this._syncSelfStatusRequestTime) {
      return;
    }

    const userStatus = this.store.state.live.userStatus;
    // 只记录自己的权限变更或全员权限变更
    if (userId !== userStatus?.user_id && userId !== "*") {
      return;
    }

    if (!this._pendingPermissionChanges) {
      this._pendingPermissionChanges = {};
    }

    // 合并权限变更
    Object.assign(this._pendingPermissionChanges, permissionChanges);

    uploadLiveLog({
      message: `${action} - 记录请求期间的权限变更`,
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: action,
        component: "MemberStatusService",
        userId: userId,
        permissionChanges: permissionChanges,
        pendingChanges: this._pendingPermissionChanges,
      },
    });
  }

  /**
   * 同步自己的状态（从接口获取）
   * 因为自己发送的广播消息自己收不到，所以需要从接口获取
   * 注意：需要使用接口返回的完整状态，包括权限字段（is_send_audio、is_send_video等）
   * 因为连接断开期间，老师可能已经修改了你的权限
   *
   * 处理竞态条件：如果接口请求期间权限被修改，接口返回后使用最新的权限
   */
  syncSelfStatus(roomId) {
    // 记录请求开始时间，用于判断权限变更是否发生在请求期间
    this._syncSelfStatusRequestTime = Date.now();
    this._pendingPermissionChanges = null;
    getUserStatus({ room_id: roomId })
      .then((res) => {
        if (res.code === 1 && res.data) {
          const userStatus = this.store.state.live.userStatus;
          let statusData = {
            ...res.data,
            user_id: userStatus?.user_id || res.data.user_id,
          };

          // 检查请求期间是否有权限变更
          // 如果有，使用最新的权限变更（因为发生在接口请求之后，更准确）
          if (this._pendingPermissionChanges) {
            uploadLiveLog({
              message: "syncSelfStatus - 检测到请求期间的权限变更",
              level: "info",
              tag: "allMemberStatusMap",
              extra: {
                action: "syncSelfStatus",
                component: "MemberStatusService",
                apiData: res.data,
                pendingChanges: this._pendingPermissionChanges,
              },
            });

            // 合并权限变更（pendingChanges 优先级更高，因为更新）
            statusData = {
              ...statusData,
              ...this._pendingPermissionChanges,
            };
          }

          uploadLiveLog({
            message: "syncSelfStatus success",
            level: "info",
            tag: "allMemberStatusMap",
            extra: {
              action: "syncSelfStatus",
              component: "MemberStatusService",
              statusData: statusData,
              apiData: res.data,
              hasPendingChanges: !!this._pendingPermissionChanges,
            },
          });
          // 如果没有权限，状态都是关闭状态
          // 如果有权限，状态需要根据beforeStatus设置，如果之前是开启状态，则保持开启状态，如果之前是关闭状态，则保持关闭状态
          // 更新自己的状态到 store（包含所有权限字段）

          const beforeStatus = this.store.state.live.userStatus;
          const newStatus = {
            ...statusData,
            video_state:
              statusData.is_send_video === 1 ? beforeStatus.video_state : 2,
            audio_state:
              statusData.is_send_audio === 1 ? beforeStatus.audio_state : 2,
          };
          this.store.commit("live/setUserStatus", newStatus);
          this.imService.sendMGLiveRoomCustomMsg({
            cmdId: 2,
            data: {
              type: "initUserStatus",
              data: {
                ...newStatus,
              },
            },
          });
          // 清理标记
          this._syncSelfStatusRequestTime = null;
          this._pendingPermissionChanges = null;
        }
      })
      .catch((error) => {
        uploadLiveLog({
          message: "syncSelfStatus failed",
          level: "error",
          tag: "allMemberStatusMap",
          extra: {
            action: "syncSelfStatus",
            component: "MemberStatusService",
            error: error.message || String(error),
          },
        });
        // 清理标记
        this._syncSelfStatusRequestTime = null;
        this._pendingPermissionChanges = null;
      });
  }

  /**
   * 通过 IM 广播消息，通知所有人发送自己的最新状态
   * 所有人收到消息后会主动发送自己的状态，包括自己
   */
  requestOtherUsersStatus() {
    if (!this.imService) {
      uploadLiveLog({
        message: "requestOtherUsersStatus skipped - no imService",
        level: "warn",
        tag: "allMemberStatusMap",
        extra: {
          action: "requestOtherUsersStatus",
          component: "MemberStatusService",
        },
      });
      return;
    }

    uploadLiveLog({
      message: "requestOtherUsersStatus",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "requestOtherUsersStatus",
        component: "MemberStatusService",
      },
    });

    // 发送请求状态的消息
    this.imService.sendMGLiveRoomCustomMsg({
      cmdId: 2,
      data: {
        type: "getInitUserStatus",
        data: {
          userId: "*",
        },
      },
    });
    // 兼容旧版本
    this.imService.sendMGLiveRoomCustomMsg({
      cmdId: 2,
      data: {
        type: "getUserStatus",
        data: {
          userId: "*",
        },
      },
    });
  }
  /**
   * 初始化获取举手人员列表
   */
  initRaiseHandList() {
    const roomInfo = this.store.state.live.roomInfo;
    if (!roomInfo?.id) {
      return;
    }
    getRoster({
      room_id: roomInfo.id,
      hand_up: 1,
    })
      .then((res) => {
        const raiseHandList = res.data.list || [];
        raiseHandList.forEach((handItem) => {
          // 判断是否在10秒内举手
          // 举手剩余时间
          const remainingTime =
            10000 - (Date.now() - handItem.hand_up_time * 1000);
          if (remainingTime > 0) {
            this.updateMemberStatusMap(
              {
                user_id: handItem.user_id,
                is_hand_up: 1,
              },
              "initRaiseHandList"
            );
            setTimeout(() => {
              this.updateMemberStatusMap(
                {
                  user_id: handItem.user_id,
                  is_hand_up: 0,
                },
                "initRaiseHandList"
              );
            }, remainingTime);
          }
        });
      })
      .catch((error) => {
        console.error("初始化举手人员列表失败", error);
      });
  }
  /**
   * 销毁服务，清理所有监听器
   */
  destroy() {
    uploadLiveLog({
      message: "MemberStatusService destroy start",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "destroy",
        component: "MemberStatusService",
      },
    });

    this.removeImServiceListeners();
    this.removeLiveServiceListeners();
    this.removeEventBusListeners();

    this.imService = null;
    this.liveService = null;
    this.userStatus = null;

    uploadLiveLog({
      message: "MemberStatusService destroy completed",
      level: "info",
      tag: "allMemberStatusMap",
      extra: {
        action: "destroy",
        component: "MemberStatusService",
      },
    });
  }
}
